import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Row, CardBody, Card } from 'reactstrap';
import { Tooltip, IconButton, Button } from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PersonPinIcon from '@mui/icons-material/PersonPin';

import RadioSelectTable from 'src/shared/components/table/RadioSelectTable';

const MAPPING_COLUMNS = [
  {
    key: 'driverName',
    header: 'Driver Name',
    sortable: true,
  },
  {
    key: 'userName',
    header: 'User Name',
    sortable: true,
  },
  {
    key: 'vehicleId',
    header: 'Vehicle Status',
    width: '120px',
    align: 'center',
    sortable: false,
    render: (value) => (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        {value != null ? (
          <Tooltip title="Vehicle is assigned">
            <DirectionsCarIcon style={{ color: '#10b981', fontSize: '20px' }} />
          </Tooltip>
        ) : (
          <Tooltip title="Vehicle is not assigned">
            <DirectionsCarIcon style={{ color: '#94a3b8', fontSize: '20px' }} />
          </Tooltip>
        )}
      </div>
    ),
  },
  {
    key: 'vehiclePlateNo',
    header: 'Plate No.',
    sortable: true,
    render: (value) => value || '—'
  },

];

const DriverVehicleMapping = ({
  reactTableData,
  isNoRecordFound,
  onAssign,
  onUnassign
}) => {
  const rows = reactTableData?.tableRowsData ?? [];

  const [selectedDriver, setSelectedDriver] = useState(
    rows.length > 0 ? rows[0] : null
  );

  // Sync state if selected driver changes or list refreshes
  const currentSelectedDriver = rows.find(r => r.driverId === selectedDriver?.driverId) || (rows.length > 0 ? rows[0] : null);

  return (
    <Col md={12} lg={12} style={{ marginTop: '20px' }}>
      <Row>
        {/* ── Left: Master List ───────────────────────────── */}
        <Col md={7} lg={7}>
          <RadioSelectTable
            rows={rows}
            columns={MAPPING_COLUMNS}
            rowKey="driverId"
            selectedKey={currentSelectedDriver?.driverId}
            onSelect={setSelectedDriver}
            searchKeys={['driverName', 'userName', 'vehiclePlateNo', 'employerName']}
            searchPlaceholder="Search by Driver name, username, plate, agency..."
            sortKey="driverName"
            headerTitle="Driver Mappings"
            pageSizes={[5, 10, 20, 50]}
          />
        </Col>

        {/* ── Right: Detail Panel ─────────────────────────── */}
        <Col md={5} lg={5}>
          <Card style={{
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
            minHeight: '520px',
            background: '#ffffff',
            overflow: 'hidden',
          }}>
            {currentSelectedDriver ? (
              <>
                {/* Header Banner */}
                <div style={{
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                  padding: '24px 20px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <AccountCircleIcon style={{ color: '#ffffff', fontSize: '2.5rem', opacity: 0.9 }} />
                      <div>
                        <h4 style={{ margin: 0, fontWeight: 800, fontSize: '1.2rem', color: '#ffffff' }}>
                          {currentSelectedDriver.driverName}
                        </h4>
                        <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)' }}>
                          @{currentSelectedDriver.userName}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <span
                        className={`badge ${currentSelectedDriver.status ? 'bg-success' : 'bg-danger'}`}
                        style={{ fontSize: '0.75rem', fontWeight: 700 }}
                      >
                        {currentSelectedDriver.status ? 'ACTIVE' : 'IN-ACTIVE'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Details Body */}
                <CardBody style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                    {/* Rider details block */}
                    <div>
                      <h6 className="text-muted text-uppercase fw-bold mb-2"
                        style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        Driver Mapping Info
                      </h6>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Rider Agency:</span>
                          <span className="fw-bold small text-dark d-flex align-items-center gap-1">
                            <BusinessIcon style={{ fontSize: '14px', color: '#64748b' }} />
                            {currentSelectedDriver.employerName || '—'}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Rider Status:</span>
                          <span className="fw-bold small text-dark d-flex align-items-center gap-1">
                            <PersonPinIcon style={{ fontSize: '14px', color: '#64748b' }} />
                            {currentSelectedDriver.riderStatus || '—'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Vehicle assignment info & controls */}
                    <div>
                      <h6 className="text-muted text-uppercase fw-bold mb-2"
                        style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        Vehicle Assignment Mapping
                      </h6>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', textAlign: 'center' }}>
                        
                        {currentSelectedDriver.vehicleId != null ? (
                          <>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                              <DirectionsCarIcon style={{ color: '#10b981', fontSize: '3rem' }} />
                              <span style={{ fontSize: '1.1rem', fontWeight: 800, color: '#1e293b' }}>
                                Plate No: {currentSelectedDriver.vehiclePlateNo}
                              </span>
                              <span className="text-muted small">
                                Vehicle is currently assigned and mapped to this driver
                              </span>
                            </div>

                            <Button
                              variant="outlined"
                              size="medium"
                              color="error"
                              startIcon={<RemoveIcon />}
                              onClick={() => onUnassign(currentSelectedDriver)}
                              style={{ textTransform: 'none', fontWeight: 600, width: '100%', marginTop: '8px' }}
                            >
                              Unassign Vehicle
                            </Button>
                          </>
                        ) : (
                          <>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                              <DirectionsCarIcon style={{ color: '#94a3b8', fontSize: '3rem' }} />
                              <span style={{ fontSize: '1.05rem', fontWeight: 700, color: '#64748b' }}>
                                No Vehicle Assigned
                              </span>
                              <span className="text-muted small">
                                Driver does not have a vehicle mapped
                              </span>
                            </div>

                            <Button
                              variant="contained"
                              size="medium"
                              color="primary"
                              startIcon={<AddIcon />}
                              onClick={() => onAssign(currentSelectedDriver)}
                              style={{ textTransform: 'none', fontWeight: 600, width: '100%', marginTop: '8px', boxShadow: 'none' }}
                            >
                              Assign Vehicle
                            </Button>

                            <div style={{
                                backgroundColor: '#fffbeb',
                                borderLeft: '4px solid #f59e0b',
                                color: '#b45309',
                                padding: '12px 14px',
                                borderRadius: '8px',
                                marginTop: '14px',
                                fontSize: '0.78rem',
                                lineHeight: 1.5,
                                textAlign: 'left',
                                width: '100%',
                            }}>
                                <strong style={{ display: 'block', marginBottom: '4px' }}>⚠️ Important Note:</strong>
                                Once the vehicle mapping is complete, please instruct the rider to download and log in to the mobile application. The rider will receive mobile notifications only after this initial login. If a duty is assigned before they log in, they will not receive any notifications.
                            </div>
                          </>
                        )}
                        
                      </div>
                    </div>

                  </div>
                </CardBody>
              </>
            ) : (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', padding: '40px', textAlign: 'center', minHeight: '520px',
              }}>
                <div style={{ fontSize: '3.5rem', color: '#cbd5e1', marginBottom: '16px' }}>🛵</div>
                <h5 className="fw-bold text-muted">No Driver Selected</h5>
                <p className="text-muted small">Select a driver from the list to view mapping options.</p>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </Col>
  );
};

DriverVehicleMapping.propTypes = {
  reactTableData: PropTypes.shape({
    tableRowsData: PropTypes.arrayOf(PropTypes.shape()),
  }),
  isNoRecordFound: PropTypes.bool,
  onAssign: PropTypes.func.isRequired,
  onUnassign: PropTypes.func.isRequired,
};

export default DriverVehicleMapping;
